import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../style";
import {
  FileText, FileCheck, Hash, CreditCard, Download, Info, Shield
} from "lucide-react";

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;

  padding: 48px 0 72px;
`;

const Hero = styled.div`
  background: #1a365d;
  border-radius: 14px;
  padding: 44px 52px;
  margin-bottom: 32px;
  animation: ${fadeUp} 0.4s ease both;
  @media (max-width: 768px) { padding: 28px 24px; }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.7);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const HeroDesc = styled.p`
  color: rgba(255,255,255,0.55);
  font-size: 14px;
  margin: 0;
  line-height: 1.65;
  max-width: 560px;
`;

// ─── Section ──────────────────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 24px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${p => p.$delay || "0s"};
`;

const SectionTitle = styled.h2`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #718096;
  margin: 0 0 12px;
  padding-left: 4px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
`;

// ─── Doc Row ──────────────────────────────────────────────────────────────────

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #eef1f6;
  transition: background 0.15s;

  &:last-child { border-bottom: none; }
  &:hover { background: #f9fafb; }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const DocIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${p => p.$bg || "#ebf4ff"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$color || "#2c5282"};
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a365d;
  margin: 0 0 3px;
`;

const DocMeta = styled.p`
  font-size: 12px;
  color: #a0aec0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #cbd5e0;
  display: inline-block;
`;

const LangBadges = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    margin-left: 58px;
  }
`;

const LangBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 13px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid #e2eaf4;
  background: #f7f9fc;
  color: #2c5282;
  transition: background 0.15s, border-color 0.15s, transform 0.12s;
  white-space: nowrap;

  &:hover {
    background: #ebf4ff;
    border-color: #bee3f8;
    transform: translateY(-1px);
  }
`;

// ─── Notice ───────────────────────────────────────────────────────────────────

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid #e2eaf4;
  border-left: 3px solid #2c5282;
  border-radius: 10px;
  padding: 16px 20px;
  margin-top: 24px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: 0.25s;
`;

const NoticeIcon = styled.div`
  color: #2c5282;
  flex-shrink: 0;
  margin-top: 1px;
`;

const NoticeText = styled.p`
  font-size: 13px;
  color: #4a5568;
  line-height: 1.65;
  margin: 0;
`;

// ─── Data ─────────────────────────────────────────────────────────────────────

const BASE = "https://abt.tj/files";

const docSections = [
  {
    key: "main",
    titleKey: "documents.sections.main.title",
    docs: [
      {
        titleKey: "documents.documentTypes.charter",
        format: "DOCX",
        size: "2.5",
        icon: FileText,
        iconBg: "#ebf4ff",
        iconColor: "#2c5282",
        links: {
          ru: `${BASE}/ustav-ru.docx`,
          tj: `${BASE}/ustav-tj.docx`,
        },
      },
      {
        titleKey: "documents.documentTypes.stateRegistration",
        format: "PDF",
        size: "1.8",
        icon: FileCheck,
        iconBg: "#e6fffa",
        iconColor: "#276749",
        links: {
          ru: `${BASE}/license-ru.pdf`,
          tj: `${BASE}/license-tj.pdf`,
        },
      },
    ],
  },
  {
    key: "tax",
   
    docs: [
      {
        titleKey: "documents.documentTypes.enn",
        format: "PDF",
        size: "1.2",
        icon: Hash,
        iconBg: "#fef3c7",
        iconColor: "#92400e",
        links: {
          ru: `${BASE}/inn-ru.pdf`,
          tj: `${BASE}/inn-tj.pdf`,
        },
      },
      {
        titleKey: "documents.documentTypes.inn",
        format: "PDF",
        size: "0.8",
        icon: CreditCard,
        iconBg: "#fce8f3",
        iconColor: "#97266d",
        links: {
          ru: `${BASE}/inn-ru.pdf`,
          tj: `${BASE}/inn-tj.pdf`,
        },
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

function Docs() {
  const { t } = useTranslation();

  return (
    <PageWrapper>
      <Container>

        <Hero>
          <HeroBadge>
            <Shield size={11} />
            {t("documents.badge", "Официальные документы")}
          </HeroBadge>
          <HeroTitle>{t("documents.title")}</HeroTitle>
          <HeroDesc>{t("documents.description")}</HeroDesc>
        </Hero>

        {docSections.map((section, si) => (
          <Section key={section.key} $delay={`${0.05 + si * 0.07}s`}>
            <SectionTitle>{t(section.titleKey)}</SectionTitle>
            <Card>
              {section.docs.map((doc, di) => {
                const Icon = doc.icon;
                return (
                  <DocRow key={di}>
                    <DocIconWrap $bg={doc.iconBg} $color={doc.iconColor}>
                      <Icon size={19} />
                    </DocIconWrap>

                    <DocInfo>
                      <DocTitle>{t(doc.titleKey)}</DocTitle>
                      <DocMeta>
                        {doc.format}
                        <MetaDot />
                        {t("documents.fileInfo.size", { size: doc.size })}
                      </DocMeta>
                    </DocInfo>

                    <LangBadges>
                      <LangBtn href={doc.links.ru} download>
                        <Download size={12} />
                        РУ
                      </LangBtn>
                      <LangBtn href={doc.links.tj} download>
                        <Download size={12} />
                        ТҶ
                      </LangBtn>
                    </LangBadges>
                  </DocRow>
                );
              })}
            </Card>
          </Section>
        ))}

        <Notice>
          <NoticeIcon><Info size={16} /></NoticeIcon>
          <NoticeText>{t("documents.notice")}</NoticeText>
        </Notice>

      </Container>
    </PageWrapper>
  );
}

export default Docs;